import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'ngx-webstorage';
import {Observable, ReplaySubject, of, Subject} from 'rxjs';
import { shareReplay, tap, catchError } from 'rxjs/operators';

import { StateStorageService } from 'app/core/auth/state-storage.service';
import { ApplicationConfigService } from '../config/application-config.service';
import { Account } from 'app/core/auth/account.model';
import {UserData} from "../../models/user-data";
import {Employee} from "../../models/employee";
import {LoginService} from "../../login/login.service";
import {AlertService} from "../../shared/alert/alert.service";

@Injectable({ providedIn: 'root' })
export class AccountService {

  private userIdentity: Account | null = null;
  public authenticationState = new ReplaySubject<Account | null>(1); // TODO: OLD: PRIVATE
  private accountCache$?: Observable<Account> | null;
  private _loggedSubject: Subject<void>;

  constructor(
    private translateService: TranslateService,
    private sessionStorageService: SessionStorageService,
    private http: HttpClient,
    private stateStorageService: StateStorageService,
    private router: Router,
    private applicationConfigService: ApplicationConfigService,
    private loginService: LoginService,
    private alertService: AlertService,
  ) {
    this._loggedSubject = new Subject<void>();
  }

  public getLoggedUser(): Observable<UserData>{
    return this.http.get<UserData>("services/portaleamministrazione/api/pn-utente/logged-user");
  }

  authenticate(identity: Account | null): void {
    this.userIdentity = identity;
    this.authenticationState.next(this.userIdentity);
    if (!identity) {
      this.accountCache$ = null;
    }
  }

  hasAnyAuthority(authorities: string[] | string): boolean {
    if (!this.userIdentity) {
      return false;
    }
    if (!Array.isArray(authorities)) {
      authorities = [authorities];
    }
    return this.userIdentity.authorities.some((authority: string) => authorities.includes(authority));
  }

  identity(force?: boolean): Observable<Account | null> {
    if (!this.accountCache$ || force) {
      this.accountCache$ = this.fetch().pipe(
        tap({
          next: (account: Account) => { // ho fatto l'accesso da keyclock
              this.authenticate(account);

              // After retrieve the account info, the language will be changed to
              // the user's preferred language configured in the account setting
              // unless user have choosed other language in the current session
              if (!this.sessionStorageService.retrieve('locale')) {
                this.translateService.use(account.langKey);
              }

              this.navigateToStoredUrl();
          },
          //2023-11-06 gestione del caso in cui arrivi un utente per cui la login va in eccezione
          //esempio: un utente che arriva da keycloak senza codice fiscale
          error: (err) =>{
            if(err.error.status === 500 ){
			  setTimeout(()=>{this.loginService.logout();},4000);
			  this.alertService.creaAlert(err.error.detail,"danger","sticky",4000,true);

            }
          }
        }),
        shareReplay()
      );
    }
    return this.accountCache$.pipe(catchError(() => of(null)));
  }

  isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.authenticationState.asObservable();
  }

  private fetch(): Observable<Account> {
    return this.http.get<Account>(this.applicationConfigService.getEndpointFor('api/account'));
  }

  get loggedSubject(): Subject<void> {
    return this._loggedSubject;
  }

  set loggedSubject(value: Subject<void>) {
    this._loggedSubject = value;
  }

  private navigateToStoredUrl(): void {
    // previousState can be set in the authExpiredInterceptor and in the userRouteAccessService
    // if login is successful, go to stored previousState and clear previousState
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl.replace("/ca", ""));
    }
  }
}
